<template>
  <div id="dictionary-app" class="container updates-container">
    <ImageBanner
      :settingsPage="true"
      disableAuth
    ></ImageBanner>

    <div class="updates-content">
      <div v-if="loading" class="loading-message">
        <font-awesome-icon icon="spinner" class="spinner-icon"/> Loading updates...
      </div>

      <div v-else-if="error" class="error-message">
        Error loading updates: {{ error }}
      </div>

      <div v-else class="markdown-content" v-html="renderedContent"></div>
    </div>
  </div>
</template>

<script>
import ImageBanner from '../components/ImageBanner';

export default {
  name: 'UpdatesView',
  components: {
    ImageBanner
  },
  data() {
    return {
      loading: true,
      error: null,
      content: '',
      renderedContent: ''
    }
  },
  async mounted() {
    try {
      // Load the UPDATES.md file from public folder
      // Use process.env.BASE_URL to get the correct base path
      const basePath = process.env.BASE_URL || '/';
      const response = await fetch(`${basePath}UPDATES.md`);

      if (!response.ok) {
        throw new Error('Failed to load updates file');
      }

      this.content = await response.text();
      this.renderedContent = this.parseMarkdown(this.content);
      this.loading = false;
    } catch (err) {
      console.error('Error loading updates:', err);
      this.error = err.message;
      this.loading = false;
    }
  },
  methods: {
    parseMarkdown(markdown) {
      // Simple markdown parser for basic formatting
      let html = markdown;

      // Escape HTML
      html = html.replace(/&/g, '&amp;')
                 .replace(/</g, '&lt;')
                 .replace(/>/g, '&gt;');

      // Headers
      html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
      html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
      html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

      // Bold
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Horizontal rules
      html = html.replace(/^---$/gim, '<hr>');

      // Process lists with proper nesting
      html = this.processLists(html);

      // Line breaks
      html = html.replace(/\n\n/g, '</p><p>');
      html = '<p>' + html + '</p>';

      // Clean up extra p tags around other block elements
      html = html.replace(/<p>(<h[1-6]>)/g, '$1');
      html = html.replace(/(<\/h[1-6]>)<\/p>/g, '$1');
      html = html.replace(/<p>(<ul>)/g, '$1');
      html = html.replace(/(<\/ul>)<\/p>/g, '$1');
      html = html.replace(/<p><hr><\/p>/g, '<hr>');

      return html;
    },
    processLists(text) {
      const lines = text.split('\n');
      const result = [];
      const listStack = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const match = line.match(/^(\t*| *)- (.*)$/);

        if (match) {
          // Count indentation level (tabs or 2 spaces = 1 level)
          const indent = match[1];
          const level = indent.replace(/  /g, '\t').split('\t').length - 1;
          const content = match[2];

          // Close lists if we've decreased indentation
          while (listStack.length > level + 1) {
            result.push('</ul>');
            listStack.pop();
          }

          // Open new list if we've increased indentation
          if (listStack.length < level + 1) {
            result.push('<ul>');
            listStack.push(level);
          }

          result.push(`<li>${content}</li>`);
        } else {
          // Close all open lists when we hit a non-list line
          while (listStack.length > 0) {
            result.push('</ul>');
            listStack.pop();
          }
          result.push(line);
        }
      }

      // Close any remaining open lists
      while (listStack.length > 0) {
        result.push('</ul>');
        listStack.pop();
      }

      return result.join('\n');
    }
  }
}
</script>

<style scoped>
.updates-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.updates-content {
  background: #ffffff;
  padding: 30px;
  border-radius: 8px;
  margin-top: 30px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.loading-message,
.error-message {
  text-align: center;
  padding: 40px;
  font-size: 1.2rem;
}

.error-message {
  color: #dc3545;
}

.spinner-icon {
  font-size: 24px;
  margin-right: 10px;
}

.markdown-content {
  text-align: left;
  line-height: 1.6;
  color: #333;
}

.markdown-content >>> h1 {
  font-size: 2rem;
  margin-top: 0;
  margin-bottom: 20px;
  color: #2c3e50;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 10px;
}

.markdown-content >>> h2 {
  font-size: 1.6rem;
  margin-top: 30px;
  margin-bottom: 15px;
  color: #34495e;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.markdown-content >>> h3 {
  font-size: 1.3rem;
  margin-top: 25px;
  margin-bottom: 12px;
  color: #555;
}

.markdown-content >>> p {
  margin-bottom: 15px;
}

.markdown-content >>> ul {
  margin-left: 20px;
  margin-bottom: 15px;
  list-style-type: none;
}

.markdown-content >>> ul ul {
  margin-top: 8px;
  margin-bottom: 8px;
  margin-left: 25px;
}

.markdown-content >>> li {
  margin-bottom: 8px;
  padding-left: 0;
  position: relative;
}

.markdown-content >>> li::before {
  content: "•";
  color: #007bff;
  font-weight: bold;
  display: inline-block;
  width: 1em;
  margin-left: -1em;
  position: absolute;
  left: 0;
}

.markdown-content >>> ul ul li::before {
  content: "◦";
  color: #6c757d;
}

.markdown-content >>> strong {
  font-weight: 600;
  color: #2c3e50;
}

.markdown-content >>> hr {
  border: none;
  border-top: 2px solid #e0e0e0;
  margin: 30px 0;
}

@media only screen and (max-width: 768px) {
  .updates-container {
    padding: 10px;
  }

  .updates-content {
    padding: 15px;
  }

  .markdown-content >>> h1 {
    font-size: 1.5rem;
  }

  .markdown-content >>> h2 {
    font-size: 1.3rem;
  }

  .markdown-content >>> h3 {
    font-size: 1.1rem;
  }
}
</style>
